import React from 'react';
import { FaArrowDown, FaBell, FaWallet, FaChevronRight, FaArrowUp } from 'react-icons/fa';

/**
 * HeroDashboardMockup
 */
export default function HeroDashboardMockup({ className = "" }) {
  return (
    <div className={`w-full min-w-0 flex items-start justify-center select-none overflow-hidden lg:overflow-visible ${className}`}>
      {/* Responsive scaler: the inner stage is a fixed 560x420 design that
          scales down on smaller screens via transform. */}
      <div className="relative w-full max-w-[560px] h-[210px] min-[360px]:h-[240px] sm:h-[303px] md:h-[370px] lg:h-[420px]">
        <div className="absolute left-1/2 top-0 w-[560px] h-[420px] -translate-x-1/2 origin-top scale-[0.50] min-[360px]:scale-[0.57] sm:scale-[0.72] md:scale-[0.88] lg:scale-100">
          <Stage />
        </div>
      </div>
    </div>
  )
}

function Stage() {
  return (
    <div
      className="relative mx-auto"
      style={{
        width: 560,
        height: 420,
        perspective: "1000px",
        transformStyle: "preserve-3d",
        overflow: "visible",
      }}
    >
      {/* ---------- Background glow + orbit lines ---------- */}
      <BackgroundFX />

      {/* ---------- Card 1: Toplam Varlık ---------- */}
      <GlassCard
        style={{
          top: 25,
          left: 30,
          width: 245,
          height: 140,
          transform: "rotateX(5deg) rotateY(-8deg) rotateZ(-1deg)",
        }}
      >
        <CardLabel>Toplam Varlık</CardLabel>
        <div className="mt-1 flex items-baseline gap-1">
          <span className="text-2xl font-bold tracking-tight text-slate-900">₺128.750</span>
          <span className="text-sm font-semibold text-slate-500">,45</span>
        </div>
        <p className="text-[11px] font-medium text-slate-400">Toplam Değer</p>
        <div className="mt-auto flex items-center gap-2 pt-2">
          <Badge>
            <FaArrowUp className="h-2 w-2" />
            2,45%
          </Badge>
          <span className="text-[11px] font-medium text-slate-400">Son 30 gün</span>
        </div>
      </GlassCard>

      {/* ---------- Card 2: Performans ---------- */}
      <GlassCard
        style={{
          top: 35,
          right: 20,
          width: 245,
          height: 140,
          transform: "rotateX(5deg) rotateY(-7deg) rotateZ(1deg)",
        }}
      >
        <CardLabel>Performans (30 Gün)</CardLabel>
        <PerformanceChart />
        <div className="mt-auto flex items-center justify-between pt-1">
          <Badge>
            <FaArrowUp className="h-2 w-2" />
            2,45%
          </Badge>
          <div className="text-right leading-tight">
            <p className="text-sm font-bold text-slate-900">₺3.078,22</p>
            <p className="text-[10px] font-medium text-slate-400">Kazanç</p>
          </div>
        </div>
      </GlassCard>

      {/* ---------- Card 3: Döviz Çevirici ---------- */}
      <GlassCard
        style={{
          top: 190,
          left: 55,
          width: 245,
          height: 175,
          transform: "rotateX(6deg) rotateY(-8deg) rotateZ(-1deg)",
        }}
      >
        <CardLabel>Döviz Çevirici</CardLabel>
        <div className="mt-2 flex flex-col gap-1.5">
          <CurrencyRow flag="🇺🇸" code="USD" />
          <div className="flex justify-center">
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-violet-100 text-violet-600">
              <FaArrowDown className="h-2 w-2" />
            </div>
          </div>
          <CurrencyRow flag="🇹🇷" code="TRY" />
        </div>
        <p className="mt-auto pt-2 text-center text-[11px] font-semibold text-violet-600">
          1 USD = 32,52 TRY
        </p>
      </GlassCard>

      {/* ---------- Card 4: Varlık & Uyarılar ---------- */}
      <GlassCard
        style={{
          top: 185,
          right: 35,
          width: 235,
          height: 170,
          transform: "rotateX(6deg) rotateY(-7deg) rotateZ(1deg)",
        }}
      >
        <CardLabel>Varlık &amp; Uyarılar</CardLabel>
        <div className="mt-2 flex flex-col gap-2">
          <StatRow
            icon={<FaWallet className="h-3 w-3" />}
            label="Varlıklar"
            value="12"
            tone="violet"
          />
          <StatRow
            icon={<FaBell className="h-3 w-3" />}
            label="Uyarılar"
            value="3"
            tone="amber"
          />
        </div>
        <button
          type="button"
          className="mt-auto flex w-full items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white/70 py-1.5 text-[11px] font-semibold text-slate-600 transition-colors hover:bg-white"
        >
          Detaylara Git
          <FaChevronRight className="h-2 w-2" />
        </button>
      </GlassCard>
    </div>
  )
}

/* ----------------------------------------------------------------- */
/* Background: glow + orbit ellipses + glowing platform ring          */
/* ----------------------------------------------------------------- */
function BackgroundFX() {
  return (
    <div className="pointer-events-none absolute inset-0" style={{ transform: "translateZ(-1px)" }}>
      {/* central radial glow */}
      <div
        className="absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
        style={{
          background:
            "radial-gradient(circle, rgba(167,139,250,0.55) 0%, rgba(139,92,246,0.25) 40%, rgba(139,92,246,0) 70%)",
        }}
      />

      {/* orbit lines */}
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 560 420"
        fill="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="orbit" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="rgba(196,181,253,0.0)" />
            <stop offset="50%" stopColor="rgba(196,181,253,0.7)" />
            <stop offset="100%" stopColor="rgba(196,181,253,0.0)" />
          </linearGradient>
        </defs>
        <ellipse cx="280" cy="210" rx="250" ry="120" stroke="url(#orbit)" strokeWidth="1.2" />
        <ellipse
          cx="280"
          cy="210"
          rx="210"
          ry="95"
          stroke="url(#orbit)"
          strokeWidth="1"
          opacity="0.7"
        />
        <ellipse
          cx="280"
          cy="210"
          rx="160"
          ry="65"
          stroke="url(#orbit)"
          strokeWidth="0.8"
          opacity="0.5"
        />

        {/* orbit dots */}
        <circle cx="530" cy="210" r="3" fill="#e9d5ff" />
        <circle cx="62" cy="180" r="2.5" fill="#c4b5fd" />
        <circle cx="430" cy="305" r="2" fill="#ddd6fe" />
        <circle cx="150" cy="300" r="2.5" fill="#e9d5ff" />
      </svg>

      {/* glowing platform ring under the cards */}
      <div
        className="absolute left-1/2 top-[330px] h-[70px] w-[380px] -translate-x-1/2 rounded-[50%] blur-md"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(167,139,250,0.55) 0%, rgba(139,92,246,0.0) 70%)",
        }}
      />
      <div
        className="absolute left-1/2 top-[345px] h-[40px] w-[300px] -translate-x-1/2 rounded-[50%] border border-violet-300/40"
        style={{ boxShadow: "0 0 40px 6px rgba(167,139,250,0.35)" }}
      />
    </div>
  )
}

/* ----------------------------------------------------------------- */
/* Reusable glass card                                                */
/* ----------------------------------------------------------------- */
function GlassCard({
  children,
  style,
}) {
  return (
    <div
      className="absolute flex flex-col rounded-2xl border border-white/60 bg-white/85 p-3.5 backdrop-blur-md"
      style={{
        ...style,
        boxShadow:
          "0 20px 45px -15px rgba(76,29,149,0.45), 0 8px 18px -10px rgba(76,29,149,0.35), inset 0 1px 0 rgba(255,255,255,0.7)",
        transformStyle: "preserve-3d",
      }}
    >
      {children}
    </div>
  )
}

function CardLabel({ children }) {
  return <p className="text-xs font-semibold text-slate-500">{children}</p>
}

function Badge({ children }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 px-1.5 py-0.5 text-[11px] font-bold text-emerald-600">
      {children}
    </span>
  )
}

function CurrencyRow({ flag, code }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white/70 px-2.5 py-1.5">
      <div className="flex items-center gap-2">
        <span className="text-base leading-none">{flag}</span>
        <span className="text-sm font-semibold text-slate-700">{code}</span>
      </div>
      <FaChevronRight className="h-3 w-3 rotate-90 text-slate-400" />
    </div>
  )
}

function StatRow({
  icon,
  label,
  value,
  tone,
}) {
  const tones = {
    violet: "bg-violet-100 text-violet-600",
    amber: "bg-amber-100 text-amber-600",
  }
  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white/70 px-2.5 py-1.5">
      <div className="flex items-center gap-2">
        <span className={`flex h-6 w-6 items-center justify-center rounded-md ${tones[tone]}`}>
          {icon}
        </span>
        <span className="text-xs font-medium text-slate-600">{label}</span>
      </div>
      <span className="text-sm font-bold text-slate-900">{value}</span>
    </div>
  )
}

/* ----------------------------------------------------------------- */
/* Smooth green line chart with gradient area                         */
/* ----------------------------------------------------------------- */
function PerformanceChart() {
  return (
    <svg
      className="my-1 h-12 w-full"
      viewBox="0 0 200 50"
      fill="none"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="perfArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(16,185,129,0.35)" />
          <stop offset="100%" stopColor="rgba(16,185,129,0)" />
        </linearGradient>
      </defs>
      {/* area */}
      <path
        d="M0 38 C 20 36, 30 30, 50 32 S 80 22, 100 24 S 130 12, 150 16 S 180 4, 200 8 L 200 50 L 0 50 Z"
        fill="url(#perfArea)"
      />
      {/* line */}
      <path
        d="M0 38 C 20 36, 30 30, 50 32 S 80 22, 100 24 S 130 12, 150 16 S 180 4, 200 8"
        stroke="#10b981"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  )
}
